import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../../../contexts/ToastContext';
import GestionPedidoBase from '../shared/GestionPedidoBase';
import './PedidoDelibot.css';

/**
 * Componente para crear/editar pedidos de Delibot
 * Ahora usa GestionPedidoBase para la lógica centralizada
 */
const CreacionPedidoDelibot = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const inicializacionCompleta = useRef(false);

  // ===== ESTADOS ESPECÍFICOS DE PEDIDOS DELIBOT =====
  const [numeroOrden, setNumeroOrden] = useState('');
  const [nombreCliente, setNombreCliente] = useState('');
  const [clienteCompleto, setClienteCompleto] = useState('');
  const [modalClienteVisible, setModalClienteVisible] = useState(false);
  const [numeroOrdenTemp, setNumeroOrdenTemp] = useState('');
  const [nombreClienteTemp, setNombreClienteTemp] = useState('');

  // Estados del pedido específicos para Delibot
  const [modoEdicion, setModoEdicion] = useState(false);
  const [pedidoExistente, setPedidoExistente] = useState(null);
  const [numeroCuenta, setNumeroCuenta] = useState('00');
  const [fechaHora, setFechaHora] = useState(new Date());

  // ===== EFECTOS =====

  // Efecto para actualizar fecha y hora
  useEffect(() => {
    const interval = setInterval(() => {
      setFechaHora(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Cargar datos iniciales al montar
  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  // ===== FUNCIONES DE CARGA DE DATOS =====

  const cargarDatosIniciales = useCallback(async () => {
    if (inicializacionCompleta.current) return;

    try {
      // Verificar datos del estado de navegación (modo edición)
      const datosGuardados = location.state?.datosPedido;

      if (datosGuardados) {
        const datos = datosGuardados;

        // Configurar modo edición si hay un pedido existente
        if (datos.esEdicion && datos.pedidoCreado) {
          setModoEdicion(true);
          setPedidoExistente(datos.pedidoCreado);
          setNumeroCuenta(datos.pedidoCreado.numeroDia || '00');

          // Extraer número de orden y nombre del clienteDelibot o clienteLlevar
          let clienteDelibot = datos.pedidoCreado.clienteDelibot ||
                              datos.pedidoCreado.clienteLlevar ||
                              datos.numeroOrden ||
                              datos.numeroDelibot || '';

          // Si clienteDelibot está vacío, usar valores por defecto
          if (!clienteDelibot) {
            clienteDelibot = 'DEL-' + (datos.pedidoCreado.numeroDia || '00000');
          }

          const partes = clienteDelibot.split(' - ');
          if (partes.length >= 2) {
            setNumeroOrden(partes[0]);
            setNombreCliente(partes[1]);
          } else {
            setNumeroOrden(clienteDelibot);
            setNombreCliente('');
          }
          setClienteCompleto(clienteDelibot);
        } else {
          // Modo crear nuevo, pero con datos de cliente
          setNumeroOrden(datos.numeroOrden || '');
          setNombreCliente(datos.nombreCliente || '');
          setClienteCompleto(datos.numeroOrden ? `${datos.numeroOrden}${datos.nombreCliente ? ' - ' + datos.nombreCliente : ''}` : '');
        }
      } else {
        // Si no hay datos guardados, mostrar modal de cliente
        setTimeout(() => {
          setModalClienteVisible(true);
        }, 500);
      }

      inicializacionCompleta.current = true;
    } catch (err) {
      console.error("Error al cargar datos iniciales:", err);
      toast.error("Error al cargar datos iniciales del pedido");
    }
  }, [toast]);

  // ===== CONFIGURACIÓN PARA GESTIÓN PEDIDO BASE =====

  // Configuración específica para pedidos Delibot
  const configuracion = useMemo(() => ({
    permitirCambioMesa: false,           // No aplica para Delibot
    permitirCambioMesero: false,         // No hay mesero en Delibot
    permitirCambioPersonas: false,       // Siempre 1 persona para Delibot
    permitirDatosCliente: true,          // SÍ necesita datos de cliente
    permitirDireccion: false,            // No necesita dirección (es delivery externo)
    permitirRepartidor: false,           // Delibot maneja su propio delivery
    requiereImpresion: true,             // SÍ requiere impresión de comandas
    permitirPagoMultiple: true           // SÍ permite múltiples formas de pago
  }), []);

  // Datos del pedido para GestionPedidoBase
  const datosPedido = useMemo(() => {
    const datos = {
      idPedido: pedidoExistente?.idPedido || null,
      numeroPedido: numeroCuenta,
      status: pedidoExistente?.status || (modoEdicion ? 'abierto' : 'nuevo'),
      // Datos específicos de pedido Delibot
      clienteDelibot: clienteCompleto,
      clienteLlevar: clienteCompleto, // Para que se inserte en la base de datos
      numeroDelibot: numeroOrden || (clienteCompleto ? clienteCompleto.split(' - ')[0] : null), // Para compatibilidad con backend
      numeroOrden: numeroOrden || (clienteCompleto ? clienteCompleto.split(' - ')[0] : null),
      nombreCliente: nombreCliente || (clienteCompleto && clienteCompleto.includes(' - ') ? clienteCompleto.split(' - ')[1] : null),
      numeroPersonas: 1, // Siempre 1 para Delibot
      fechaCreacion: fechaHora,
      // Para compatibilidad con infoPedido en TomaPedidos
      numeroMesa: 'DELIBOT',
      nombreMesa: 'Delibot',
      mesa: {
        numero: 'DELIBOT',
        nombre: 'Delibot'
      },
      mesero: null // No se usa mesero en pedidos Delibot
    };

    return datos;
  }, [pedidoExistente, numeroCuenta, modoEdicion, clienteCompleto, numeroOrden, nombreCliente, fechaHora]);

  // ===== CALLBACKS PARA GESTIÓN PEDIDO BASE =====

  const onPedidoCreado = useCallback((nuevoPedido) => {
    toast.success("Pedido Delibot creado correctamente");

    // Actualizar estados locales con el nuevo pedido
    if (nuevoPedido.numeroDia) {
      setNumeroCuenta(nuevoPedido.numeroDia);
    }

    // IMPORTANTE: Actualizar pedidoExistente con el nuevo pedido que incluye idPedido
    if (nuevoPedido.idPedido) {
      setPedidoExistente(nuevoPedido);
      setModoEdicion(true); // Cambiar a modo edición ya que ahora existe el pedido
    }
  }, [toast]);

  const onPedidoActualizado = useCallback((pedidoActualizado) => {
    toast.success("Pedido Delibot actualizado correctamente");

    // Actualizar el estado local del pedido con los nuevos datos
    if (pedidoActualizado && pedidoExistente) {
      setPedidoExistente(prev => ({
        ...prev,
        ...pedidoActualizado
      }));
    }
  }, [toast, pedidoExistente]);

  const onPedidoCerrado = useCallback((resultado) => {
    toast.success("Pedido Delibot finalizado correctamente");

    // Actualizar el estado local del pedido con el nuevo status
    if (resultado && pedidoExistente) {
      setPedidoExistente(prev => ({
        ...prev,
        status: 'pendiente' // Pedidos Delibot se cambian a pendiente al finalizar
      }));
    }
  }, [toast, pedidoExistente]);

  // ===== MANEJADORES ESPECÍFICOS DEL MODAL DE CLIENTE =====

  const confirmarCliente = useCallback(() => {
    if (!numeroOrdenTemp.trim()) {
      toast.warning("El número de orden de Delibot es obligatorio");
      return;
    }

    const numeroLimpio = numeroOrdenTemp.trim();
    const nombreLimpio = nombreClienteTemp.trim();
    const clienteCompletoNuevo = nombreLimpio ?
      `${numeroLimpio} - ${nombreLimpio}` :
      numeroLimpio;

    setNumeroOrden(numeroLimpio);
    setNombreCliente(nombreLimpio);
    setClienteCompleto(clienteCompletoNuevo);
    setModalClienteVisible(false);

    toast.success(`Pedido Delibot: ${clienteCompletoNuevo}`);
  }, [numeroOrdenTemp, nombreClienteTemp, toast]);

  const cancelarCliente = useCallback(() => {
    if (!clienteCompleto) {
      // Si no hay cliente configurado, regresar a la lista
      navigate('/ventas');
      return;
    }

    setModalClienteVisible(false);
    setNumeroOrdenTemp(numeroOrden);
    setNombreClienteTemp(nombreCliente);
  }, [clienteCompleto, numeroOrden, nombreCliente, navigate]);

  const editarCliente = useCallback(() => {
    setNumeroOrdenTemp(numeroOrden);
    setNombreClienteTemp(nombreCliente);
    setModalClienteVisible(true);
  }, [numeroOrden, nombreCliente]);

  // ===== VALIDACIÓN DE CLIENTE =====

  const validarCliente = useCallback(() => {
    if (!clienteCompleto) {
      toast.error("Debe proporcionar los datos del pedido Delibot");
      setModalClienteVisible(true);
      return false;
    }
    return true;
  }, [clienteCompleto, toast]);

  // ===== RENDER =====

  // Si no hay cliente configurado y no está mostrando el modal, mostrar carga
  if (!clienteCompleto && !modalClienteVisible) {
    return (
      <div className="toma-pedidos-cargando">
        <div className="spinner"></div>
        <p>Configurando pedido Delibot...</p>
      </div>
    );
  }

  return (
    <>
      {/* Usar GestionPedidoBase con configuración específica para Delibot */}
      <GestionPedidoBase
        tipoPedido="delibot"
        datosPedido={datosPedido}
        configuracion={configuracion}
        onPedidoCreado={onPedidoCreado}
        onPedidoActualizado={onPedidoActualizado}
        onPedidoCerrado={onPedidoCerrado}
        rutaRegreso="/ventas"
        // Función de validación personalizada antes de ciertas acciones
        validacionesPersonalizadas={{
          antesDeGuardar: validarCliente,
          antesDeImprimir: validarCliente,
          antesDeFinalizar: validarCliente
        }}
        // Función para mostrar/editar cliente
        onEditarCliente={editarCliente}
      />

      {/* Modal de datos del cliente - específico para Delibot */}
      {modalClienteVisible && (
        <div className="modal-overlay">
          <div className="modal-container modal-cliente">
            <div className="modal-header">
              <h2>Datos del Pedido Delibot</h2>
              <button
                className="close-button"
                onClick={cancelarCliente}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="numeroOrdenInput">
                  Número de Orden Delibot *
                </label>
                <input
                  type="text"
                  id="numeroOrdenInput"
                  value={numeroOrdenTemp}
                  onChange={(e) => setNumeroOrdenTemp(e.target.value)}
                  className="cliente-input"
                  placeholder="Ej: DEL-12345"
                  autoFocus
                  maxLength="50"
                />
              </div>

              <div className="form-group">
                <label htmlFor="nombreClienteInput">
                  Nombre del Cliente (opcional)
                </label>
                <input
                  type="text"
                  id="nombreClienteInput"
                  value={nombreClienteTemp}
                  onChange={(e) => setNombreClienteTemp(e.target.value)}
                  className="cliente-input"
                  placeholder="Ingrese el nombre del cliente"
                  maxLength="100"
                />
              </div>

              <div className="cliente-preview">
                <strong>Se guardará como:</strong>
                <span>
                  {numeroOrdenTemp.trim() ?
                    (nombreClienteTemp.trim() ?
                      `${numeroOrdenTemp.trim()} - ${nombreClienteTemp.trim()}` :
                      numeroOrdenTemp.trim()
                    ) :
                    'Ingrese al menos el número de orden'
                  }
                </span>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="accept-button"
                onClick={confirmarCliente}
                disabled={!numeroOrdenTemp.trim()}
              >
                ✓ Confirmar
              </button>
              <button
                className="cancel-button"
                onClick={cancelarCliente}
              >
                ✗ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreacionPedidoDelibot;